import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SponsorsShowcase from '../components/SponsorsShowcase';
import BackButton from '../components/BackButton';
import { Building2, Hammer, TreeDeciduous, Home, Shield, ArrowRight, Award, FileText, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const BatimentExpertisePage = () => {
  const [heroRef, heroInView] = useInView();
  const [branchesRef, branchesInView] = useInView();
  const [expertiseRef, expertiseInView] = useInView();
  const [garantiesRef, garantiesInView] = useInView();

  const branches = [
    { icon: Hammer, title: 'BATI\'SCOP', subtitle: 'Construction & Réhabilitation', desc: 'Construction neuve, réhabilitation et rénovation énergétique des bâtiments.', gradient: 'from-amber-500 to-orange-500', features: ['Construction neuve', 'Réhabilitation', 'Rénovation énergétique'] },
    { icon: TreeDeciduous, title: 'O\'SCOP Industrie bois', subtitle: 'Produits pour l\'habitat', desc: 'Menuiserie, charpente et transformation du bois pour l\'habitat ultramarin.', gradient: 'from-lime-500 to-green-500', features: ['Menuiserie', 'Charpente', 'Transformation bois'] },
    { icon: Home, title: 'IMMO\'SCOP', subtitle: 'Habitat solidaire', desc: 'Programmes immobiliers, habitat solidaire et solutions d\'accession sociale.', gradient: 'from-indigo-500 to-purple-500', features: ['Programmes immobiliers', 'Accession sociale', 'Habitat solidaire'] },
  ];

  const expertises = [
    { icon: Eye, title: 'Expertise civile', desc: 'Diagnostics et évaluations techniques selon le droit civil.' },
    { icon: FileText, title: 'Rapports techniques', desc: 'Documents d\'expertise pour vos projets et litiges.' },
    { icon: Shield, title: 'Prévention', desc: 'Anticipation des risques et accompagnement préventif.' },
    { icon: Award, title: 'Certification', desc: 'Experts qualifiés et certifiés pour vos besoins.' },
  ];

  const garanties = [
    { title: 'RCD Solidaire', desc: 'Responsabilité Civile Décennale', gradient: 'from-blue-500 to-cyan-500' },
    { title: 'DOS', desc: 'Dommage Ouvrage Solidaire', gradient: 'from-green-500 to-emerald-500' },
    { title: 'BATICO', desc: 'Solidité des ouvrages', gradient: 'from-purple-500 to-pink-500' },
    { title: 'CAP BÂTI+', desc: 'Livraison & conformité', gradient: 'from-amber-500 to-orange-500' },
    { title: 'DOMIUM', desc: 'Aide post-travaux', gradient: 'from-red-500 to-rose-500' },
    { title: 'CCMI Solidaire', desc: 'Constructions nouvelles', gradient: 'from-indigo-500 to-purple-500' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1920&q=80" 
            alt="Construction site" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-amber-600/20 to-orange-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-300 border border-amber-500/30 mb-6 px-4 py-2">
              <Building2 className="w-4 h-4 mr-2" /> Filières bâtiment
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-amber-100 to-white bg-clip-text text-transparent">Bâtiment &</span>
              <br />
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Expertise Civile</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mb-10 leading-relaxed">
              Construction, réhabilitation et <span className="text-amber-400 font-semibold">expertise technique</span> pour 
              sécuriser vos projets immobiliers dans les Outre-mer.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
                Nous contacter <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg transition-all duration-300" onClick={() => window.location.href = '/garanties/coppam'}>
                Découvrir COPPAM
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Branches Section */}
      <section ref={branchesRef} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${branchesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 mb-4 px-4 py-2"><Hammer className="w-4 h-4 mr-2" /> Nos branches</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">3 branches <span className="text-amber-400">bâtiment</span></h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {branches.map((branch, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 hover:border-amber-500/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${branchesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${branch.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <branch.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">{branch.title}</h3>
                <p className="text-sm text-amber-400 mb-4">{branch.subtitle}</p>
                <p className="text-slate-400 leading-relaxed mb-6">{branch.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {branch.features.map((feature, i) => (
                    <span key={i} className="text-xs bg-slate-700/50 text-slate-300 px-3 py-1 rounded-full">{feature}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section ref={expertiseRef} className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-purple-600/10 to-transparent rounded-full blur-3xl" /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-1000 ${expertiseRef ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 mb-4 px-4 py-2"><Eye className="w-4 h-4 mr-2" /> Expertise civile</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Notre <span className="text-purple-400">expertise</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {expertises.map((item, index) => (
              <div key={index} className={`bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 hover:border-purple-500/50 transition-all duration-500 hover:-translate-y-2 text-center ${expertiseInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4 border border-purple-500/30">
                  <item.icon className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Garanties Section */}
      <section ref={garantiesRef} className="py-24 bg-gradient-to-br from-amber-600 via-orange-600 to-amber-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Garanties <span className="text-amber-200">COPPAM</span></h2>
            <p className="text-xl text-white/80">Sécurisez vos projets avec nos garanties solidaires</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {garanties.map((garantie, index) => (
              <div key={index} className={`bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-1 text-center ${garantiesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 50}ms` }}>
                <div className={`w-10 h-10 mx-auto rounded-lg bg-gradient-to-br ${garantie.gradient} flex items-center justify-center mb-3`}>
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-sm font-bold text-white mb-1">{garantie.title}</h3>
                <p className="text-xs text-white/60">{garantie.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button size="lg" className="bg-white text-amber-600 hover:bg-slate-100 font-bold px-10 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/garanties/coppam'}>
              Découvrir COPPAM <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Un projet <span className="text-amber-400">bâtiment</span> ?</h2>
          <p className="text-xl text-slate-400 mb-10">Contactez-nous pour étudier votre projet et vous accompagner.</p>
          <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-10 py-6 text-lg shadow-2xl shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
            Nous contacter <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <SponsorsShowcase variant="branch" branche="BATICO" />
      <Footer />
    </div>
  );
};

export default BatimentExpertisePage;
